import React, { useEffect, useState } from 'react';
import { Dropdown } from 'lib/components';
import { Flex } from 'lib/components/styles/layout';
import {
  ParagraphSmall500,
  ParagraphSmall,
} from 'lib/components/styles/typography';
import { MdContentCopy, MdEdit } from 'react-icons/md';
import { IoMdEye } from 'react-icons/io';
import { DropdownLabelWrapper, LabelText } from 'lib/components/folders/style';
import { LabelOption } from './types';
import {
  AccessOptions,
  AutomotiveRoleOptions,
} from 'lib/api/folderAccess/types';
import { useTheme } from 'styled-components/macro';
import { DropdownForFolderShare } from 'lib/components/dropdown/DropdownForFolderShare';
import { useTranslation } from 'react-i18next';

type Props = {
  accessOptionsData: AccessOptions[];
  shareWithOptionsData: AutomotiveRoleOptions[];
  setSelectedValues: (value: AutomotiveRoleOptions[]) => void;
  selectedValues: AutomotiveRoleOptions[];
  setSelectedAccess: (value: LabelOption) => void;
  selectedAccess: LabelOption;
};

export const ShareWithAndFolderAccess = ({
  setSelectedValues,
  selectedValues,
  accessOptionsData,
  shareWithOptionsData,
  setSelectedAccess,
  selectedAccess,
}: Props) => {
  const [dropdownAccessData, setDropdownAccessData] = useState<LabelOption[]>(
    []
  );

  const themes = useTheme();
  const { t } = useTranslation();
  const iconMap: Record<string, React.ElementType> = {
    IoMdEye,
    MdContentCopy,
    MdEdit,
  };

  useEffect(() => {
    const mappedAccessData = accessOptionsData.map((item: AccessOptions) => {
      const Icon = iconMap[item.iconClassForWeb];
      return {
        value: item.id.toString(),
        label: (
          <DropdownLabelWrapper>
            <Icon
              className={item.iconClassForWeb}
              color={themes.colors.neutral[100]}
              style={{ width: '20px', height: '20px' }}
            />
            <LabelText>{item.name}</LabelText>
          </DropdownLabelWrapper>
        ),
      };
    });

    setDropdownAccessData(mappedAccessData);

    if (mappedAccessData.length > 0) {
      setSelectedAccess(mappedAccessData[0]);
    }
  }, [accessOptionsData]);

  const CustomLabel = (
    data: any,
    formatOptionMeta: { context: 'menu' | 'value' }
  ) => {
    const { context } = formatOptionMeta;
    if (context === 'menu') {
      return (
        <Flex gap='8px' padding='12px 8px 12px 8px' flexDirection='row'>
          <ParagraphSmall500 width='160px'>{data.label}</ParagraphSmall500>
          {data?.targetType === 'user' && (
            <ParagraphSmall width='115px' color={themes.colors.neutral[40]}>
              {data.email}
            </ParagraphSmall>
          )}
        </Flex>
      );
    }
    return data.label;
  };

  return (
    <Flex
      flexDirection='row'
      justifyContent='space-between'
      alignItems='center'
      width='100%'
      gap='8px'
    >
      <Flex gap='10px' width='499px'>
        <ParagraphSmall500 color={themes.colors.neutral[80]}>
          {t('Share With*')}
        </ParagraphSmall500>
        <DropdownForFolderShare
          placeholder={t('Role')}
          value={selectedValues}
          onChange={setSelectedValues}
          options={shareWithOptionsData}
          isMulti={true}
          formatOptionLabel={CustomLabel}
        />
      </Flex>

      <Flex width='287px' gap='10px'>
        <ParagraphSmall500 color={themes.colors.neutral[80]}>
          {t('Access*')}
        </ParagraphSmall500>
        <Dropdown
          extendStyles={{
            option: {
              padding: '0px',
            },
            menuList: {
              padding: '20px',
              gap: '10px',
              display: 'flex',
              flexDirection: 'column',
              border: '1px',
            },
          }}
          height={40}
          creatable={false}
          isSearchable={false}
          className='dropdown'
          placeholder={t('Select Option')}
          zIndexProp={1}
          value={selectedAccess}
          onChange={value => setSelectedAccess(value)}
          options={dropdownAccessData}
          formatOptionLabel={(
            data: any,
            formatOptionMeta: { context: 'menu' | 'value' }
          ) => {
            const { context } = formatOptionMeta;
            if (context === 'menu') {
              return (
                <Flex
                  gap='8px'
                  padding='12px 8px 12px 8px'
                  flexDirection='row'
                  width='100%'
                >
                  <ParagraphSmall500>{data.label}</ParagraphSmall500>
                </Flex>
              );
            }
            return data.label;
          }}
        />
      </Flex>
    </Flex>
  );
};
